import CancelIcon from "@mui/icons-material/Clear";
import DownloadingIcon from "@mui/icons-material/Downloading";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllotmentValueByStationWorkgroupAndYear,
  fetchDeclarationBalanceImportStatus,
  fetchDeclarationWindowDates,
  fetchEmployeesByStationWorkgroupWithDeclarations,
} from "../api/employee";
import { isUserAuthorizedByServer } from "../api/userAccess";
import PageLoading from "../components/PageLoading";
import StarsErrorIcon from "../components/StarsErrorIcon";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import DeclarationBalanceImport from "../components/declarations/DeclarationBalanceImport";
import DeclarationTimer from "../components/declarations/DeclarationTimer";
import DeclarationsTable from "../components/declarations/DeclarationsTable";
import EditDeclarationDialog from "../components/declarations/EditDeclarationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import { selectWorkgroupData } from "../slices/commonConfig/commonConfigSlice";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import { getAdminStation, getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import {
  BREADCRUMBS_TITLE,
  DECLARATION_IMPORT_STATUSES,
  HOME_PATH,
  IAM_CONTRACT,
  MULTI_STATION_ADMIN_ACCESS,
  TWU_CONTRACT,
} from "../util/constants";
import { MISSING_DECLARATIONS } from "../util/declarations/declarationConstants";
import {
  filterAllIssuesInDeclarations,
  isDeclarationWindowClosed,
  scrubDeclarationsData,
} from "../util/declarations/declarationUtils";
import {
  exportBlankCsv,
  exportToCsv,
  generateExportedFilename,
  generateExportedFilenameForBlankDeclarationCsv,
} from "../util/declarations/declarationsExport";
import { initializeSearchIndex, searchDeclarations } from "../util/declarations/searchDeclarations";
import { getSortedDeclarationsByName } from "../util/declarations/sortDeclarations";
import { declarationsDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import { validateShiftWorkgroup, validateStation } from "../util/inputValidation";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  IGNORE_UNWANTED_FIELDS_IN_DECLARATION_PAGES_IAM,
  IGNORE_UNWANTED_FIELDS_IN_DECLARATION_PAGES_TWU,
} from "../util/messageConstants";
import {
  getStationTimeZoneLuxonDateTimeFromIsoDateTimeString,
  getTimeZoneFromSwaStation,
} from "../util/timeZoneService";
import { getUnionContractGivenWorkgroup } from "../util/workgroupMapper";

const DECLARATIONS_TABLE_HEIGHT = "calc(100vh - 330px)";
const DECLARATIONS_TABLE_HEIGHT_WITH_WARNINGS = "calc(100vh - 385px)";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    position: "fixed",
    width: "100%",
  },
  breadCrumb: {
    marginLeft: "2.5vw",
  },
  desktopTitle: {
    marginLeft: "4.2vw",
  },
  declarationsFilterAndTitle: {
    minHeight: "170px",
  },
  textInput: {
    maxWidth: "17vw",
    margin: "auto",
    textAlign: "left",
  },
  stationTextInput: {
    width: "90px",
  },
  workGroupTextInput: {
    width: "180px",
  },
  searchTextInput: {
    width: "250px",
  },
  declarationsInput: {
    margin: "5px",
  },
  declarationsInputRow: {
    marginTop: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginLeft: "3.9vw",
    marginRight: "3.9vw",
  },
  declarationsFilters: {
    display: "flex",
  },
  csvButtons: {
    display: "flex",
    alignItems: "center",
  },
  declarationsBox: {
    marginTop: "10px",
    margin: "auto",
    width: "100%",
  },
  declarations: {
    width: "92.1vw",
    marginLeft: "4.2vw",
  },
  issuesToggle: {
    "&:hover": {
      backgroundColor: theme.palette.secondary.swaLightBlue,
    },
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  },
  issueSelected: {
    backgroundColor: theme.palette.secondary.swaLightBlue,
  },
}));

const DeclarationAdminstrationPage = (props) => {
  const classes = useStyles();

  // Redux State
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const workgroupData = useSelector(selectWorkgroupData);
  const dispatch = useDispatch();

  // Declarations Data State
  const [declarations, setDeclarations] = useState([]);
  const [filteredDeclarations, setFilteredDeclarations] = useState([]);
  const [displayedDeclarations, setDisplayedDeclarations] = useState([]);

  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editEmployeeData, setEditEmployeeData] = useState("");
  const [saveErrors, setSaveErrors] = useState("");
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});

  // Filter State
  const [station, setStation] = useState("");
  const [stationTouched, setStationTouched] = useState(false);
  const [stationErrors, setStationErrors] = useState("");
  const [workgroup, setWorkgroup] = useState("");
  const [workgroupErrors, setWorkgroupErrors] = useState("");
  const [declarationYear, setDeclarationYear] = useState("");

  const [searchString, setSearchString] = useState("");
  const [isSearch, setIsSearch] = useState(false);

  const [missingDeclarationsCount, setMissingDeclarationsCount] = useState(0);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [issuesFilter, setIssuesFilter] = useState("");
  const isApiSubscribedRef = useRef(true);
  const [declarationWindowDates, setDeclarationWindowDates] = useState([null, null]);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [calcDailyAllotments, setCalcDailyAllotments] = useState("NA");

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      const station = getAdminStation(props.featureConfig.userStations, userSession, stationFilter);
      setStation(station);
      if (declarationWindowDates[0] === null || declarationWindowDates[1] === null) {
        getDeclarationWindowDates();
      }
    }
    return () => {
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    initializeSearchIndex(filteredDeclarations);
    const newSearchStringTrimmed = searchString.trim().toLowerCase();

    if (isSearch) {
      searchDeclarations({
        searchString: newSearchStringTrimmed,
        applySearchCallback: applySearch,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDeclarations]);

  useEffect(() => {
    setSearchString("");
  }, [station, workgroup, declarationYear]);

  useEffect(() => {
    if (station !== "" && workgroup !== "" && declarationYear !== "") {
      updateDeclarations(station, workgroup, declarationYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station, workgroup, declarationYear]);

  useEffect(() => {
    if (declarationWindowDates[0] && declarationWindowDates[1] && workgroup) {
      let currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(userSession.activeStation));
      if (declarationYear !== "") {
        // Enable Declarations edit for
        // 1. current year
        // 2. upcoming declaration year from 7 days before window open
        if (
          currentDateTime.year === parseInt(declarationYear) ||
          (currentDateTime.year + 1 === parseInt(declarationYear) &&
            currentDateTime >
              declarationWindowDates[0].minus({
                days: process.env.REACT_APP_ENABLE_DECLARATION_ROUND_YEAR_OPTION_PRIOR_TO_DECLARATION_OPEN,
              }))
        ) {
          setIsReadOnly(false);
        } else {
          setIsReadOnly(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declarationYear]);

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.station === station &&
      responseQueryParams.workgroup === workgroup &&
      responseQueryParams.declarationYear === declarationYear
    );
  };

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const getDeclarationWindowDates = () => {
    fetchDeclarationWindowDates()
      .then((res) => res.json())
      .then((result) => {
        if (result?.response) {
          const declarationWindowStart = getStationTimeZoneLuxonDateTimeFromIsoDateTimeString(
            result.response.declarationWindowStart
          );
          const declarationWindowEnd = getStationTimeZoneLuxonDateTimeFromIsoDateTimeString(
            result.response.declarationWindowEnd
          );
          setDeclarationWindowDates([declarationWindowStart, declarationWindowEnd]);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
      });
  };

  const updateDeclarations = async (station, workgroup, declarationYear) => {
    isApiSubscribedRef.current = true;
    setIsLoading(true);
    setIsError(false);
    setErrorObject({});
    setFilteredDeclarations([]);
    setMissingDeclarationsCount(0);
    setIssuesFilter("");
    setDeclarations([]);
    setDisplayedDeclarations([]);

    try {
      const declarationsRes = await fetchEmployeesByStationWorkgroupWithDeclarations(
        station,
        workgroup,
        declarationYear
      );
      const declarationsData = await declarationsRes.json();

      if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(declarationsData.queryParams)) {
        const declarations = scrubDeclarationsData(declarationsData.results);
        setDeclarations(declarations);
        setFilteredDeclarations(declarations);
        setDisplayedDeclarations(declarations);

        const contract = getUnionContractGivenWorkgroup(workgroup);
        let missingDeclarationsCount = [];

        if (contract === IAM_CONTRACT) {
          missingDeclarationsCount = declarations.filter(
            (emp) => !emp.projectedVacationBalance || !emp.datWeeksCount || !emp.blockWeeksCount
          );
        } else if (contract === TWU_CONTRACT) {
          missingDeclarationsCount = declarations.filter(
            (emp) =>
              !emp.declaration ||
              !emp.datWeeksCount ||
              !emp.blockWeeksCount ||
              !emp.projectedVacationBalance ||
              !emp.projectedFtoBalance
          );
        }

        setMissingDeclarationsCount(missingDeclarationsCount.length);
      }

      const allotmentRes = await fetchAllotmentValueByStationWorkgroupAndYear(station, workgroup, declarationYear);
      const allotmentData = await allotmentRes.json();

      if (allotmentData?.pk) {
        setCalcDailyAllotments(allotmentData.allotmentValue);
      } else {
        setCalcDailyAllotments("NA");
      }
    } catch (error) {
      console.error("Error in updateDeclarations:", error);
      setIsError(true);
      setErrorObject(declarationsDataErrorCodeToErrorMessageMap.unexpectedDeclarationsDataError);
      setCalcDailyAllotments("NA");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStationChange = (event) => {
    setStation(event.target.value);
    dispatch(updateStationFilter(event.target.value));
  };

  const handleStationBlur = (event) => {
    setStationErrors(validateStation(event.target.value));
    setStationTouched(true);
  };

  const handleWorkgroupSelectChange = (event) => {
    setWorkgroup(event.target.value);
    setWorkgroupErrors(validateShiftWorkgroup(event.target.value));
    let currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(userSession.activeStation));
    if (currentDateTime > declarationWindowDates[0] && currentDateTime < declarationWindowDates[1]) {
      setDeclarationYear((currentDateTime.year + 1).toString());
    } else {
      setDeclarationYear(currentDateTime.year.toString());
    }
  };

  const handleWorkgroupSelectBlur = (event) => {
    setWorkgroupErrors(validateShiftWorkgroup(event.target.value));
  };

  const handleSearchChange = (event) => {
    setSearchString(event.target.value);
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(event.target.value);
    }
  };

  const handleSearchCancel = () => {
    setSearchString("");
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    const newSearchStringTrimmed = newSearchString.trim().toLowerCase();
    searchDeclarations({
      searchString: newSearchStringTrimmed,
      applySearchCallback: applySearch,
    });
  };

  const applySearch = (searchString, searchedDeclarations) => {
    if (searchString === "") {
      setIsSearch(false);
    } else {
      setIsSearch(true);
    }
    setDisplayedDeclarations(searchedDeclarations);
  };

  const handleIssuesFilter = (issueType) => {
    //unselect issues filter if it is clicked 2 times
    if (issueType === issuesFilter) {
      setIssuesFilter("");
      setFilteredDeclarations(declarations);

      if (isSearch) {
        handleSearchSubmit(searchString);
      } else {
        setDisplayedDeclarations(declarations);
      }
    } else {
      let filteredList = filterAllIssuesInDeclarations(
        declarations,
        getUnionContractGivenWorkgroup(workgroup),
        issueType
      );
      setIssuesFilter(issueType);
      setFilteredDeclarations(filteredList);
      setDisplayedDeclarations(filteredList);
    }
  };

  const getDeclarationYearAllOptions = () => {
    let declarationYearAllOptions = [];
    let currYear = DateTime.now().setZone(getTimeZoneFromSwaStation(userSession.activeStation)).year;
    for (let year = currYear - 3; year <= currYear + 1; year++) {
      if (year > 2024) {
        declarationYearAllOptions.push(year.toString());
      }
    }
    return declarationYearAllOptions;
  };

  const handleDownloadEmptyCsvButtonClick = () => {
    try {
      const unionContract = getUnionContractGivenWorkgroup(workgroup);

      exportBlankCsv({
        filename: generateExportedFilenameForBlankDeclarationCsv({
          unionContract,
        }),
        unionContract,
        station,
        workgroup,
      });
    } catch (e) {
      console.error("Error during downloading csv!");
    }
  };

  const handleExportCsvButtonClick = () => {
    try {
      exportToCsv({
        filename: generateExportedFilename({
          station,
          workgroup,
          declarationYear,
        }),
        rows: [...declarations].sort(getSortedDeclarationsByName),
        station,
        workgroup,
      });
    } catch (e) {
      console.error("Error during downloading csv!");
    }
  };

  const handleEditPencilIconClick = (emp) => {
    setEditEmployeeData(emp);
    setShowEditDialog(true);
  };

  const handleRefreshBalanceImportStatus = () => {
    fetchDeclarationBalanceImportStatus(station, workgroup, declarationYear)
      .then((res) => res.json())
      .then((response) => {
        if (!response.message && response.declarationImportStatus.status === DECLARATION_IMPORT_STATUSES.SUCCESS) {
          updateDeclarations(station, workgroup, declarationYear);
        }
      })
      .catch((err) => {
        console.error("error fetching Declaration Balance Import Status!", err);
      });
  };

  const handleClickEditDialogClose = () => {
    setShowEditDialog(false);
    setEditEmployeeData("");
  };

  const renderStationItem = (station) => {
    return (
      <MenuItem key={station} value={station} disabled={!props.featureConfig.userStations.includes(station)}>
        {station}
      </MenuItem>
    );
  };

  const renderWorkgroupItem = (workgroup) => {
    return (
      <MenuItem key={workgroup} value={workgroup}>
        {workgroup}
      </MenuItem>
    );
  };

  const renderDeclarations = (declarations) => (
    <DeclarationsTable
      declarations={declarations}
      isLoading={isLoading}
      isError={isError}
      errorObject={errorObject}
      station={station}
      handleEditPencilIconClick={handleEditPencilIconClick}
      contract={workgroup ? getUnionContractGivenWorkgroup(workgroup) : null}
    />
  );

  const SearchButton = () => {
    return (
      <IconButton size="small" className="search-icon-button" onMouseDown={() => handleSearchSubmit(searchString)}>
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleSearchCancel}>
        <CancelIcon />
      </IconButton>
    );
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.DECLARATION_ADMINISTRATION];
  const breadcrumbsLink = [HOME_PATH];
  const renderMobileDeclarationsPage = () => {
    return (
      <div className="page-error" data-testid="declarations-mobile-page">
        {ADMIN_MOBILE_WARNING_MESSAGE}
      </div>
    );
  };

  const renderCalculatedDailyAllotmentsValue = () => {
    return (
      <>
        {workgroup &&
          (getUnionContractGivenWorkgroup(workgroup) === IAM_CONTRACT
            ? `Calculated Daily DAT Allotments: ${calcDailyAllotments}`
            : `Calculated Daily DAT/FTO Allotments: ${calcDailyAllotments}`)}
      </>
    );
  };

  const renderDesktopDeclarationsPage = () => {
    return (
      <div className={classes.root}>
        <div className={classes.breadCrumb}>
          <StarsBreadcrumbs
            isConfirmationRequired={false}
            breadcrumbsLinks={breadcrumbsLink}
            breadcrumbsList={breadcrumbsList}
          />
        </div>
        <div className={classes.declarationsFilterAndTitle}>
          <div
            className={
              !props.isDesktopView ? `${classes.pageTitle} page-title-left` : `page-title-left ${classes.desktopTitle}`
            }
          >
            {BREADCRUMBS_TITLE.DECLARATION_ADMINISTRATION}
          </div>
          <div className={classes.declarationsInputRow}>
            <div className={classes.declarationsFilters}>
              <div className={classes.declarationsInput}>
                {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
                  <FormControl variant="outlined">
                    <InputLabel>Station</InputLabel>
                    <Select
                      id="stationSelectElement"
                      value={station}
                      className={`${classes.textInput} ${classes.stationTextInput}`}
                      onChange={handleStationChange}
                      onBlur={handleStationBlur}
                      label="Station"
                      error={!!(stationTouched && stationErrors)}
                    >
                      <MenuItem value="" disabled>
                        <em>Station</em>
                      </MenuItem>
                      {getMultiStationAdminStationList(userSession)
                        .slice()
                        .sort()
                        .map((station) => {
                          return renderStationItem(station);
                        })}
                    </Select>
                    {stationTouched && stationErrors ? <FormHelperText error>{stationErrors}</FormHelperText> : ""}
                  </FormControl>
                ) : (
                  <TextField
                    data-testid="stationSelectElement"
                    className={
                      props.isDesktopView
                        ? `${classes.textInput} ${classes.stationTextInput}`
                        : classes.stationTextField
                    }
                    label="Station"
                    variant="outlined"
                    value={station}
                    disabled
                  />
                )}
              </div>
              <div className={classes.declarationsInput}>
                <FormControl variant="outlined">
                  <InputLabel id="workgroupSelectLabel">Workgroup</InputLabel>
                  <Select
                    labelId="workgroupSelectLabel"
                    id="workgroupSelectElement"
                    data-testid="workgroup-select-label"
                    className={`${classes.textInput} ${classes.workGroupTextInput}`}
                    value={workgroup}
                    onChange={handleWorkgroupSelectChange}
                    onBlur={handleWorkgroupSelectBlur}
                    label="Workgroup"
                    error={!!(workgroupErrors !== "")}
                  >
                    <MenuItem value="" disabled>
                      <em>Workgroup</em>
                    </MenuItem>
                    {workgroupData &&
                      Object.keys(workgroupData)
                        .filter(
                          (workGroup) =>
                            workgroupData[workGroup].unionContract === TWU_CONTRACT ||
                            workgroupData[workGroup].unionContract === IAM_CONTRACT
                        )
                        .slice()
                        .sort((a, b) => sortOnCaseSensitiveTexts(a, b))
                        .map((workgroup) => {
                          return renderWorkgroupItem(workgroup);
                        })}
                  </Select>
                  {workgroupErrors !== "" && <FormHelperText error>{workgroupErrors}</FormHelperText>}
                </FormControl>
              </div>
              <div className={classes.declarationsInput}>
                <StarsSelectInput
                  label="Year"
                  value={declarationYear}
                  allOptions={getDeclarationYearAllOptions()}
                  onChange={(event) => setDeclarationYear(event.target.value)}
                  errors={""}
                  className={`${classes.textInput} ${classes.stationTextInput}`}
                  disabled={!workgroup}
                />
              </div>
              <div className={classes.declarationsInput} data-testid="searchSelectElement">
                <TextField
                  id="searchSelectElement"
                  className={`${classes.textInput} ${classes.searchTextInput}`}
                  label="Search"
                  variant="outlined"
                  value={searchString}
                  onChange={handleSearchChange}
                  onBlur={handleSearchBlur}
                  disabled={!workgroup}
                  InputProps={{
                    endAdornment:
                      searchString !== "" ? (
                        <InputAdornment position="end">
                          <CancelButton />
                          <SearchButton />
                        </InputAdornment>
                      ) : (
                        <InputAdornment position="end">
                          <SearchButton />
                        </InputAdornment>
                      ),
                  }}
                  onKeyUp={handleSearchEnterKeyPressed}
                />
              </div>
            </div>
            <div className={classes.csvButtons}>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                <IconButton
                  sx={{ color: "primary.swaBlue" }}
                  disabled={
                    isLoading ||
                    station === "" ||
                    workgroup === "" ||
                    declarationYear === "" ||
                    isDeclarationWindowClosed(declarationYear, userSession.activeStation, declarationWindowDates[1])
                  }
                  onClick={handleRefreshBalanceImportStatus}
                >
                  <Typography variant="body2" style={{ fontSize: "12px", marginRight: "5px" }}>
                    Refresh
                  </Typography>
                  <SyncIcon />
                </IconButton>
              </div>
              <div style={{ marginLeft: "15px" }}>
                <Button
                  data-testid="declarationsPage-export-button"
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={handleDownloadEmptyCsvButtonClick}
                  startIcon={<DownloadingIcon />}
                  disabled={isLoading || !declarations.length}
                >
                  DL CSV TEMPLATE
                </Button>
              </div>
              <div style={{ marginLeft: "15px" }}>
                <DeclarationBalanceImport
                  station={station}
                  workgroup={workgroup}
                  declarations={declarations}
                  setDeclarations={setDeclarations}
                  declarationYear={declarationYear}
                  declarationWindowDates={declarationWindowDates}
                  userSession={userSession}
                ></DeclarationBalanceImport>
              </div>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={handleExportCsvButtonClick}
                  startIcon={<UploadFileIcon />}
                  disabled={isLoading || !declarations.length}
                >
                  Export CSV
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Box sx={{ textAlign: "left", paddingTop: "10px", marginLeft: "4.2vw" }}>
              {declarationWindowDates[0] !== null && declarationWindowDates[1] !== null && declarationYear && (
                <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                  <DeclarationTimer
                    windowDates={[declarationWindowDates[0], declarationWindowDates[1]]}
                    station={userSession.activeStation}
                    selectedYear={declarationYear}
                  />
                </Typography>
              )}
            </Box>
          </div>
          <div>
            {parseInt(declarationYear) === 2025 &&
              displayedDeclarations.length > 0 &&
              getUnionContractGivenWorkgroup(workgroup) === IAM_CONTRACT && (
                <Box sx={{ textAlign: "left", paddingTop: "10px", marginLeft: "4.2vw", marginRight: "3.7vw" }}>
                  <Alert severity="error">{IGNORE_UNWANTED_FIELDS_IN_DECLARATION_PAGES_IAM()}</Alert>
                </Box>
              )}
            {parseInt(declarationYear) === 2025 &&
              displayedDeclarations.length > 0 &&
              getUnionContractGivenWorkgroup(workgroup) === TWU_CONTRACT && (
                <Box sx={{ textAlign: "left", paddingTop: "10px", marginLeft: "4.2vw", marginRight: "3.7vw" }}>
                  <Alert severity="error">{IGNORE_UNWANTED_FIELDS_IN_DECLARATION_PAGES_TWU()}</Alert>
                </Box>
              )}
          </div>
          <div className={classes.declarationsBox}>
            <Grid container className={classes.declarations}>
              <Box
                sx={{
                  bgcolor: "secondary.swaLightestBlue",
                  width: "100%",
                  height: "50px",
                  borderTopLeftRadius: "5px",
                  borderTopRightRadius: "5px",
                  borderTop: "solid 1px",
                  borderLeft: "solid 1px",
                  borderRight: "solid 1px",
                  borderColor: "secondary.swaLightestBlue",
                  overflow: "hidden",
                }}
              >
                <Stack
                  sx={{ height: "100%", display: "flex", justifyContent: "left", alignItems: "center" }}
                  direction="row"
                  divider={<Divider sx={{ marginTop: "5px", marginBottom: "5px" }} orientation="vertical" flexItem />}
                >
                  {missingDeclarationsCount > 0 && (
                    <Box
                      sx={{
                        width: "30vw",
                      }}
                      onMouseDown={() => handleIssuesFilter(MISSING_DECLARATIONS)}
                      className={`${classes.issuesToggle} ${
                        issuesFilter === MISSING_DECLARATIONS && classes.issueSelected
                      }`}
                    >
                      <StarsErrorIcon />
                      <Typography component="span" sx={{ textDecoration: "underline", paddingLeft: "10px" }}>
                        {missingDeclarationsCount}
                        {` Employee${missingDeclarationsCount === 1 ? "" : "s"} with missing declarations`}
                      </Typography>
                    </Box>
                  )}
                  {missingDeclarationsCount === 0 && (
                    <Box
                      sx={{
                        width: "30vw",
                      }}
                    >
                      <Typography component="span">No data issues</Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      marginLeft: "20vw",
                      width: "40vw",
                      fontWeight: "bold",
                    }}
                  >
                    <Grid container sx={{ marginleft: "10px" }}>
                      <Grid item xs={8}>
                        {renderCalculatedDailyAllotmentsValue()}
                      </Grid>
                      <Grid item xs={4}>
                        Total Employees: {declarations.length}
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Box
              sx={{
                height:
                  declarationYear === "2025" ? DECLARATIONS_TABLE_HEIGHT_WITH_WARNINGS : DECLARATIONS_TABLE_HEIGHT,
                width: "inherit",
              }}
            >
              {renderDeclarations(displayedDeclarations)}
            </Box>
            <EditDeclarationDialog
              isDialogOpen={showEditDialog}
              onClickDialogClose={() => handleClickEditDialogClose()}
              editEmployeeData={editEmployeeData}
              setEditEmployeeData={setEditEmployeeData}
              declarationYear={declarationYear}
              setSaveErrors={setSaveErrors}
              setIsSuccessDialogOpen={setIsSuccessDialogOpen}
              isReadOnly={isReadOnly}
              declarationWindowDates={declarationWindowDates}
              station={station}
            />
            <SuccessDialog
              isSuccessDialogOpen={isSuccessDialogOpen}
              title={saveErrors !== "" ? "Error" : "Changes Saved"}
              message={"Declaration has been updated successfully!"}
              hasError={saveErrors !== ""}
              error={"Unexpected error occurred. Declaration could not be updated."}
              handleOKClick={() => {
                setIsSuccessDialogOpen(false);
                updateDeclarations(station, workgroup, declarationYear);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="declarations-admin-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopDeclarationsPage() : renderMobileDeclarationsPage()}</>
      )}
    </div>
  );
};

DeclarationAdminstrationPage.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
  featureConfig: PropTypes.object.isRequired,
};

export default DeclarationAdminstrationPage;
